/**
 * TransitionMatrixSettings Component
 * 
 * This component provides a UI for configuring allowed card transitions between different columns.
 * It displays a matrix where users can enable/disable transitions between any two states.
 * Special handling is implemented for the "Done" state, where all transitions are disabled.
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Checkbox,
  Button,
  Typography,
} from '@mui/material';
import { doc, getDoc, setDoc, deleteDoc } from 'firebase/firestore';
import { db } from '../../Components/auth/firebaseConfig';

const TransitionMatrixSettings = ({ projectId, columns, onSave, onReset }) => {
  // State to store the transition matrix configuration
  const [transitionMatrix, setTransitionMatrix] = useState(null);
  // State to track saving operation
  const [saving, setSaving] = useState(false);

  // Effect to fetch existing transition matrix or create default
  useEffect(() => {
    const fetchTransitionMatrix = async () => {
      try {
        const matrixRef = doc(db, `projectSettings/${projectId}/stateTransitions/matrix`);
        const matrixDoc = await getDoc(matrixRef);
        
        if (matrixDoc.exists()) {
          // Load existing matrix from Firestore
          const data = matrixDoc.data();
          console.log('Loading existing matrix:', data);
          setTransitionMatrix(data);
        } else {
          // Initialize default matrix with special handling for "Done" and "Closed" states
          const defaultMatrix = {};
          columns.forEach(fromColumn => {
            defaultMatrix[fromColumn] = columns.reduce((acc, toColumn) => ({
              ...acc,
              // Disable all transitions from "Done" or "Closed" states
              [toColumn]: (fromColumn === 'Done' || fromColumn === 'Closed') ? false : (fromColumn === toColumn)
            }), {});
          });
          setTransitionMatrix(defaultMatrix);
        }
      } catch (error) {
        console.error('Error fetching transition matrix:', error);
        alert('Error loading transition settings');
      }
    };

    if (projectId && columns?.length) {
      fetchTransitionMatrix();
    }
  }, [projectId, columns]);

  // Handler for toggling individual transitions
  const handleTransitionToggle = (fromColumn, toColumn) => {
    // Prevent toggling if matrix not loaded or trying to toggle from "Done" or "Closed" states
    if (!transitionMatrix || fromColumn === 'Done' || fromColumn === 'Closed') return;
    
    setTransitionMatrix(prev => ({
      ...prev,
      [fromColumn]: {
        ...prev[fromColumn],
        [toColumn]: !prev[fromColumn]?.[toColumn]
      }
    }));
  };

  // Handler for saving the transition matrix
  const saveTransitionMatrix = async () => {
    if (!transitionMatrix) return;
    setSaving(true);

    try {
      // Ensure all transitions from Done and Closed are set to false before saving
      const matrixToSave = {
        ...transitionMatrix,
        Done: columns.reduce((acc, toColumn) => ({
          ...acc,
          [toColumn]: false
        }), {})
      };

      console.log('Saving matrix:', matrixToSave);
      const matrixRef = doc(db, `projectSettings/${projectId}/stateTransitions/matrix`);
      await setDoc(matrixRef, matrixToSave);
      
      // Update local state to match saved state
      setTransitionMatrix(matrixToSave);
      
      // Notify parent component about the new matrix
      if (onSave) {
        onSave(matrixToSave);
      }
      
      alert('Transition settings saved successfully!');
    } catch (error) {
      console.error('Error saving transition matrix:', error);
      alert('Error saving transition settings');
    } finally {
      setSaving(false);
    }
  };

  // Handler for resetting the transition matrix to default
  const resetToDefault = async () => {
    // Show confirmation dialog
    if (!window.confirm('Are you sure you want to reset all transition settings to default? This will allow free card movement between all columns.')) {
      return;
    }

    setSaving(true);
    try {
      // Use parent's reset function if available
      if (onReset) {
        await onReset();
      } else {
        // Fallback to local implementation
        const matrixRef = doc(db, `projectSettings/${projectId}/stateTransitions/matrix`);
        await deleteDoc(matrixRef);
        setTransitionMatrix(null);
      }
      
      alert('Transition settings reset to default successfully!');
    } catch (error) {
      console.error('Error resetting transition matrix:', error);
      alert('Error resetting transition settings');
    } finally {
      setSaving(false);
    }
  };

  // Show loading state while matrix is being fetched
  if (!transitionMatrix) {
    return <Typography>Loading transition settings...</Typography>;
  }

  return (
    <Box sx={{ width: '100%', overflowX: 'auto', mt: 2 }}>
      {/* Title and description */}
      <Typography variant="h6" gutterBottom>
        Card State Transition Settings
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Configure which column transitions are allowed for cards. Check the boxes where transitions should be permitted.
        Note: Cards in "Done" or "Closed" states cannot be moved.
      </Typography>

      {/* Transition matrix table */}
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>From / To</TableCell>
              {columns.map((column) => (
                <TableCell key={column} align="center">{column}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {columns.map((fromColumn) => {
              const isTerminalState = fromColumn === 'Done' || fromColumn === 'Closed';
              return (
              <TableRow 
                key={fromColumn}
                // Special styling for "Done" or "Closed" state rows
                sx={isTerminalState ? { backgroundColor: '#f5f5f5' } : {}}
              >
                <TableCell 
                  component="th" 
                  scope="row"
                  // Special styling for "Done" or "Closed" state labels
                  sx={isTerminalState ? { 
                    fontStyle: 'italic',
                    color: 'text.secondary'
                  } : {}}
                >
                  {fromColumn}
                  {/* Additional note for terminal states */}
                  {isTerminalState && (
                    <Typography variant="caption" display="block" color="text.secondary">
                      (No transitions allowed)
                    </Typography>
                  )}
                </TableCell>
                {columns.map((toColumn) => (
                  <TableCell key={`${fromColumn}-${toColumn}`} align="center">
                    <Checkbox
                      checked={Boolean(transitionMatrix[fromColumn]?.[toColumn])}
                      onChange={() => handleTransitionToggle(fromColumn, toColumn)}
                      // Disable checkbox for same column or terminal states
                      disabled={fromColumn === toColumn || isTerminalState}
                      // Reduce opacity for terminal state checkboxes
                      sx={isTerminalState ? { opacity: 0.5 } : {}}
                    />
                  </TableCell>
                ))}
              </TableRow>
            )})}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Action buttons */}
      <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between' }}>
        <Button
          variant="outlined"
          color="secondary"
          onClick={resetToDefault}
          disabled={saving}
        >
          {saving ? 'Processing...' : 'Reset to Default'}
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={saveTransitionMatrix}
          disabled={saving}
        >
          {saving ? 'Saving...' : 'Save Transition Settings'}
        </Button>
      </Box>
    </Box>
  );
};

export default TransitionMatrixSettings; 